import { useCallback, useEffect, useRef, useState } from 'react'
import jwtDecode from 'jwt-decode'
import { createWS } from 'http/websockets'
import { refreshAccessToken } from 'http/authInterceptor'

/** A single socket with cleanup, bounded retries, heartbeat and JWT renewal. */
export default function useAuthenticatedSocket(path, options = {}) {
  const callbacks = useRef(options)
  callbacks.current = options
  const connection = useRef(null)
  const [status, setStatus] = useState('idle')
  const enabled = options.enabled !== false
  const send = useCallback(payload => {
    if (connection.current?.readyState !== WebSocket.OPEN) return false
    connection.current.send(typeof payload === 'string' ? payload : JSON.stringify(payload))
    return true
  }, [])

  useEffect(() => {
    if (!enabled || !path) return undefined
    let stopped = false
    let retries = 0
    let authRetries = 0
    let socket = null
    let retryTimer
    let heartbeat
    let renewal
    let connecting = false
    const clearTimers = () => { clearTimeout(retryTimer); clearInterval(heartbeat); clearTimeout(renewal) }
    const release = () => {
      clearTimers()
      if (socket) { socket.onopen = null; socket.onclose = null; socket.onerror = null; socket.onmessage = null; socket.close() }
      socket = null; connection.current = null
    }
    const connect = async (renew = false) => {
      if (stopped || connecting) return
      if (!navigator.onLine) { setStatus('offline'); return }
      connecting = true
      try {
        let token = localStorage.getItem('token')
        if (!token) { setStatus('unauthorized'); return }
        let expiry = 0
        try { expiry = jwtDecode(token).exp || 0 } catch (_) { /* refresh invalid local token */ }
        if (renew || expiry * 1000 <= Date.now() + 30000) token = await refreshAccessToken()
        if (stopped) return
        setStatus('connecting')
        socket = createWS(path)
        connection.current = socket
        socket.onopen = () => {
          if (stopped) return
          retries = 0; authRetries = 0; setStatus('connected')
          callbacks.current.onOpen?.(socket)
          heartbeat = setInterval(() => { if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ command: 'ping' })) }, 30000)
          const exp = jwtDecode(token).exp * 1000
          renewal = setTimeout(() => { refreshAccessToken().catch(() => { release(); setStatus('offline'); retryTimer = setTimeout(() => connect(true), 5000) }) }, Math.max(1000, exp - Date.now() - 30000))
        }
        socket.onmessage = event => {
          let payload
          try { payload = JSON.parse(event.data) } catch (_) { return }
          callbacks.current.onMessage?.(payload, event)
        }
        socket.onerror = event => { callbacks.current.onError?.(event) }
        socket.onclose = event => {
          clearTimers(); connection.current = null; socket = null
          callbacks.current.onClose?.(event)
          if (stopped) return
          if (event.code === 4403) { setStatus('forbidden'); return }
          if (event.code === 4401 && authRetries++ > 0) { setStatus('unauthorized'); return }
          setStatus('offline')
          retryTimer = setTimeout(() => connect(event.code === 4401), Math.min(30000, 1000 * 2 ** Math.min(retries++, 5)))
        }
      } catch (error) {
        if (!stopped) { setStatus('offline'); callbacks.current.onError?.(error); retryTimer = setTimeout(() => connect(), Math.min(30000, 1000 * 2 ** Math.min(retries++, 5))) }
      } finally { connecting = false }
    }
    const reconnect = () => {
      if (connecting || stopped) return
      release(); connect()
    }
    const authChanged = () => {
      if (!localStorage.getItem('token')) { release(); setStatus('unauthorized') }
      else if (socket) reconnect()
    }
    const storageChanged = event => { if (!event.key || event.key === 'token') authChanged() }
    const offline = () => { release(); setStatus('offline') }
    connect()
    window.addEventListener('chu:auth-token', authChanged)
    window.addEventListener('storage', storageChanged)
    window.addEventListener('online', reconnect)
    window.addEventListener('offline', offline)
    return () => { stopped = true; release(); window.removeEventListener('chu:auth-token', authChanged); window.removeEventListener('storage', storageChanged); window.removeEventListener('online', reconnect); window.removeEventListener('offline', offline) }
  }, [path, enabled])
  return { send, status }
}
